import React, { useEffect, useRef, useState } from 'react';
import { HUB_URL, TEXTOS, traducirError } from './entryMessages';

// Pantalla de entrada compartida de las apps del hub VLS.
//
// FUENTE DE VERDAD. Se COPIA a cada app con scripts/sync-ui.sh junto a
// entryMessages.js. Si se edita, correr el script para propagar y NO editar las
// copias de cada app: se pierden en el próximo sync.
//
// Deliberadamente AUTOCONTENIDA. Las 20 apps tienen configuraciones de Tailwind
// y nombres de token distintos, así que acá no se usa ninguna clase de utilidad
// de la app: los estilos van en el <style> de abajo y los colores salen de
// variables CSS con fallback (var(--color-primary, #0f7a3d)), igual que
// ConfirmDialog.jsx. Toma el color de marca de la app si existe y el verde VLS
// si no.
//
// TAMPOCO lee el AuthContext de nadie. Las apps tienen cinco formas distintas
// de contexto (hasAccess, isMember, hasCrmAccess, role, userApps); unificarlas
// es otro proyecto. Acá entra un `estado` ya calculado y dos funciones.
//
// Uso:
//   if (loading) return <EntryScreen modulo="Gerencia" estado="cargando" />;
//   if (!user)   return <EntryScreen modulo="Gerencia" estado="login" onSignIn={signIn} />;
//   if (!acceso) return <EntryScreen modulo="Gerencia" estado="sin-acceso"
//                          correo={user.email} onSignOut={signOut} />;

const CSS = `
/* El contenedor SCROLLEA. La tarjeta mide unos 460px de alto y esta capa es
   fija a la pantalla: en un teléfono acostado, o con el zoom del navegador
   arriba, la tarjeta no cabe. Un flex centrado que desborda recorta por los
   DOS lados y el de arriba queda inalcanzable, así que no se puede ni leer el
   título ni llegar al campo de correo. Los gates viejos usaban min-h-screen y
   scrolleaban; esto conserva eso. El valor "safe center" centra mientras cabe
   y se pega arriba cuando no; la línea de "center" sola que va antes queda de
   fallback para los navegadores que no entienden "safe". */
.vlse{position:fixed;inset:0;display:flex;justify-content:center;
  align-items:center;align-items:safe center;overflow-x:hidden;overflow-y:auto;padding:24px 0;
  background:linear-gradient(180deg,#FEFEFD 0%,#FAFAF9 46%,#F2F5EE 100%);
  font-family:'Source Sans 3',system-ui,-apple-system,sans-serif;
  color:var(--color-foreground,#1C1917)}

.vlse-tarjeta{position:relative;z-index:5;width:380px;max-width:88vw;padding:28px 24px;
  box-sizing:border-box;text-align:center;background:var(--color-card,#FFFFFF);
  border:1px solid var(--color-border,#E7E5E4);border-radius:14px;
  box-shadow:0 24px 48px -12px rgba(12,48,26,.28),0 4px 12px rgba(12,48,26,.12)}
@media (max-width:639px){.vlse-tarjeta{width:328px}}

.vlse-logo{height:56px;width:auto;margin:0 auto 12px;display:block}
.vlse-tit{font-family:'Plus Jakarta Sans','Source Sans 3',system-ui,sans-serif;font-weight:700;font-size:22px;
  letter-spacing:-.02em;line-height:1.15;margin:0}
.vlse-sub{font-size:14px;color:var(--color-muted-foreground,#78716C);margin:6px 0 0;line-height:1.4}
.vlse-aviso{font-size:14px;line-height:1.5;margin:10px 0 0}
.vlse-campo{margin-top:18px;text-align:left}
.vlse-lbl{display:block;font-size:13px;font-weight:500;margin-bottom:6px}
/* font-family:inherit explícito, igual que .vlse-btn. Los controles de
   formulario NO heredan la tipografía: la traen del navegador. En las dos apps
   piloto se ve bien de casualidad, porque el preflight de Tailwind ya les pone
   inherit; una app sin ese reset mostraría inputs con la fuente por defecto
   debajo de etiquetas en Source Sans 3. */
.vlse-in{width:100%;height:48px;padding:0 12px;font-size:16px;box-sizing:border-box;
  font-family:inherit;
  border:1px solid var(--color-border,#E7E5E4);border-radius:8px;
  background:var(--color-card,#FFFFFF);color:var(--color-foreground,#1C1917)}
.vlse-in:focus{outline:2px solid var(--color-primary,#0f7a3d);outline-offset:1px}
.vlse-btn{display:flex;align-items:center;justify-content:center;width:100%;height:48px;
  margin-top:20px;border:0;border-radius:8px;cursor:pointer;box-sizing:border-box;
  background:var(--color-primary,#0f7a3d);color:#fff;font-size:15px;font-weight:600;
  text-decoration:none;font-family:inherit;
  transition:transform .18s ease-out,box-shadow .18s ease-out,filter .18s ease-out}
.vlse-btn:hover:not(:disabled){transform:translateY(-1px);filter:brightness(1.06);
  box-shadow:0 5px 14px rgba(15,122,61,.35)}
/* Anillo de foco propio para Ingresar, Ir al hub y Salir. Sin esto dependían
   del que dibuja el navegador, y cualquier app con un *:focus{outline:none}
   global se lo lleva sin avisar: el botón queda enfocado y no se ve. El
   outline-offset de 2px lo saca del relleno del botón primario, que es del
   mismo color, y lo deja contra el blanco de la tarjeta. */
.vlse-btn:focus-visible{outline:2px solid var(--color-primary,#0f7a3d);outline-offset:2px}
.vlse-btn:disabled{opacity:.6;cursor:default}
.vlse-sec{background:transparent;color:var(--color-foreground,#1C1917);
  border:1px solid var(--color-border,#E7E5E4);margin-top:8px}
.vlse-sec:hover:not(:disabled){box-shadow:none;filter:none}
.vlse-err{margin-top:14px;padding:10px 12px;border-radius:8px;font-size:14px;text-align:left;
  color:#B23B2E;background:rgba(178,59,46,.08);border:1px solid rgba(178,59,46,.25)}
.vlse-pie{font-size:12px;color:var(--color-muted-foreground,#78716C);margin:16px 0 0;line-height:1.45}
`;

export default function EntryScreen({
  modulo,
  estado = 'login',
  onSignIn,
  onSignOut,
  onReintentar,
  correo,
  logo = '/vls-logo.png',
}) {
  const [email, setEmail] = useState('');
  const [clave, setClave] = useState('');
  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);
  const campo = useRef(null);

  // El foco entra apenas monta, para no retrasar a quien ya sabe qué escribir.
  useEffect(() => {
    if (estado === 'login') campo.current?.focus();
  }, [estado]);

  async function enviar(e) {
    e.preventDefault();
    // Los campos ya NO se deshabilitan mientras se envía (ver el form), así que
    // el Enter dentro de un input sigue vivo. El botón submit deshabilitado ya
    // bloquea el envío implícito en los navegadores actuales; esta guarda lo
    // cierra sin depender de ese detalle.
    if (enviando) return;
    setError('');
    setEnviando(true);
    let r;
    try {
      r = await onSignIn?.(email.trim(), clave);
    } catch (err) {
      // Un signIn de app puede lanzar en vez de devolver {error} (pasa cuando
      // la app pasa supabase.auth.signInWithPassword sin envolver y se cae la
      // red). Sin este catch la pantalla queda con el botón en "Ingresando…"
      // para siempre y sin decir nada.
      r = { error: err };
    }
    setEnviando(false);
    if (r?.error) setError(traducirError(r.error?.message));
  }

  return (
    <div className="vlse">
      <style>{CSS}</style>

      <div className="vlse-tarjeta">
        <a href={HUB_URL} title={TEXTOS.logoTitulo}>
          <img className="vlse-logo" src={logo} alt="Vista Los Sueños Adventure Park" />
        </a>
        <h1 className="vlse-tit">{modulo}</h1>

        {estado === 'cargando' && (
          <p className="vlse-sub" role="status">{TEXTOS.cargando}</p>
        )}

        {estado === 'sin-acceso' && (
          <>
            <p className="vlse-aviso">{TEXTOS.sinAcceso(modulo, correo)}</p>
            <a className="vlse-btn" href={HUB_URL}>{TEXTOS.irAlHub}</a>
            <button type="button" className="vlse-btn vlse-sec" onClick={() => onSignOut?.()}>
              {TEXTOS.salir}
            </button>
          </>
        )}

        {estado === 'error' && (
          <>
            <p className="vlse-aviso">{TEXTOS.errorAviso}</p>
            <button type="button" className="vlse-btn" onClick={() => onReintentar?.()}>
              {TEXTOS.reintentar}
            </button>
            <button type="button" className="vlse-btn vlse-sec" onClick={() => onSignOut?.()}>
              {TEXTOS.salir}
            </button>
          </>
        )}

        {estado === 'login' && (
          <>
            <p className="vlse-sub">{TEXTOS.subtitulo}</p>
            {/* Mientras se envía se deshabilita SOLO el botón, nunca los dos
                campos. Deshabilitar el elemento enfocado manda el foco al
                <body>, así que después de un intento fallido quien navega por
                teclado tenía que tabular desde el principio del documento para
                volver a la contraseña. */}
            <form onSubmit={enviar}>
              <div className="vlse-campo">
                <label className="vlse-lbl" htmlFor="vlse-correo">{TEXTOS.correo}</label>
                {/* autoComplete="username", no "email": username es lo que los
                    gestores de contraseñas emparejan con current-password para
                    ofrecer la credencial guardada, y VLS entra con cuentas de
                    rol compartidas (gerencia@, reservas@) que dependen de eso.
                    Es también lo que usaban los dos gates viejos. El type sigue
                    siendo email, por el teclado del teléfono y la validación. */}
                <input
                  id="vlse-correo"
                  ref={campo}
                  className="vlse-in"
                  type="email"
                  required
                  autoComplete="username"
                  autoCapitalize="off"
                  inputMode="email"
                  placeholder="usuario@vlscr.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="vlse-campo">
                <label className="vlse-lbl" htmlFor="vlse-clave">{TEXTOS.clave}</label>
                <input
                  id="vlse-clave"
                  className="vlse-in"
                  type="password"
                  required
                  autoComplete="current-password"
                  value={clave}
                  onChange={(e) => setClave(e.target.value)}
                />
              </div>
              {error && <div className="vlse-err" role="alert">{error}</div>}
              <button type="submit" className="vlse-btn" disabled={enviando}>
                {enviando ? TEXTOS.ingresando : TEXTOS.ingresar}
              </button>
            </form>
            <p className="vlse-pie">{TEXTOS.olvido}</p>
          </>
        )}
      </div>
    </div>
  );
}
